import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes } from 'react';
import { cn } from '../cn';

const badgeStyles = cva(
  'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-2xs font-semibold uppercase tracking-wider',
  {
    variants: {
      tone: {
        neutral: 'bg-ink-100 text-ink-700',
        brand: 'bg-brand-50 text-brand-600',
        success: 'bg-success-50 text-success-600',
        warning: 'bg-warning-50 text-warning-600',
        danger: 'bg-danger-50 text-danger-600',
        live: 'bg-purple-50 text-purple-700 ring-1 ring-purple-200',
      },
      dot: {
        true: 'pl-2',
      },
    },
    defaultVariants: {
      tone: 'neutral',
    },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeStyles> {}

export function Badge({ className, tone, dot, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeStyles({ tone, dot }), className)} {...props}>
      {dot && (
        <span
          className={cn(
            'h-1.5 w-1.5 rounded-full',
            tone === 'success' && 'bg-success-400',
            tone === 'warning' && 'bg-warning-400',
            tone === 'danger' && 'bg-danger-400',
            tone === 'brand' && 'bg-brand-500',
            (!tone || tone === 'neutral') && 'bg-ink-400',
            tone === 'live' && 'bg-purple-500',
          )}
        />
      )}
      {children}
    </span>
  );
}
